function projectPath(initiativeId) {
  return `/dashboard/owner/proyectos/${encodeURIComponent(String(initiativeId || ""))}`;
}

const OWNER_HOME_FORMS = new Set([
  "finance", "consumption", "client", "proposal", "commitment", "commitmentTime", "routine",
  "worklog", "recurring", "interaction", "initiative", "import", "inboxProcess",
]);

function failure({ failureClass, code, actionType = "render_surface", surface = "", missingFields = [], message = "" } = {}) {
  return {
    failureClass: String(failureClass || "frontend_executor"),
    code: String(code || "execution_failed"),
    actionType: String(actionType || ""),
    surface: String(surface || ""),
    missingFields: (Array.isArray(missingFields) ? missingFields : []).map(String).filter(Boolean).slice(0, 12),
    message: String(message || "").slice(0, 600),
  };
}

function navigateSafely(navigate, path, state, meta = {}) {
  try {
    navigate(path, { state });
    return { executed: true, failure: null };
  } catch (error) {
    return {
      executed: false,
      failure: failure({
        failureClass: "frontend_executor",
        code: "navigation_exception",
        ...meta,
        message: error?.message || "La navegación lanzó una excepción.",
      }),
    };
  }
}

export function executeOwnerJarvisUiOutcome(navigate, ui) {
  const surface = String(ui?.surface || "");
  if (!ui || typeof ui !== "object" || !surface) {
    return { executed: false, failure: failure({ failureClass: "frontend_surface", code: "missing_surface", surface }) };
  }
  if (typeof navigate !== "function") {
    return { executed: false, failure: failure({ failureClass: "frontend_executor", code: "navigate_unavailable", surface }) };
  }

  const token = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const state = { ownerJarvisUi: { ...ui, token } };

  if (surface === "project_pendings") {
    if (!ui.initiativeId) {
      return { executed: false, failure: failure({ failureClass: "frontend_surface", code: "missing_arguments", surface, missingFields: ["initiativeId"] }) };
    }
    try {
      window.dispatchEvent(new CustomEvent("owner:open-project-pendings", {
        detail: { initiativeId: String(ui.initiativeId), projectName: String(ui.projectName || ""), token },
      }));
      return { executed: true, failure: null };
    } catch {
      return navigateSafely(navigate, projectPath(ui.initiativeId), state, { surface });
    }
  }
  if (surface === "project360") {
    if (!ui.initiativeId) {
      return { executed: false, failure: failure({ failureClass: "frontend_surface", code: "missing_arguments", surface, missingFields: ["initiativeId"] }) };
    }
    return navigateSafely(navigate, projectPath(ui.initiativeId), state, { surface });
  }
  if (surface === "client") {
    if (!ui.clientId) {
      return { executed: false, failure: failure({ failureClass: "frontend_surface", code: "missing_arguments", surface, missingFields: ["clientId"] }) };
    }
    return navigateSafely(navigate, `/dashboard/owner/clientes?cliente=${encodeURIComponent(String(ui.clientId))}`, state, { surface });
  }
  if (surface === "owner_home") {
    const requestedForm = String(ui.form || "");
    const action = OWNER_HOME_FORMS.has(requestedForm) ? requestedForm : "";
    const outcome = navigateSafely(navigate, action ? `/dashboard/owner?jarvisAction=${encodeURIComponent(action)}` : "/dashboard/owner", state, { surface });
    if (outcome.failure || !requestedForm || action) return outcome;
    return {
      executed: true,
      failure: failure({
        failureClass: "frontend_surface",
        code: "unsupported_form",
        surface,
        message: `El formulario ${requestedForm} no está expuesto por owner_home; se abrió Owner sin el formulario pedido.`,
      }),
    };
  }
  if (surface === "route") {
    const path = String(ui.path || "");
    if (!path) {
      return { executed: false, failure: failure({ failureClass: "frontend_surface", code: "missing_arguments", surface, missingFields: ["path"] }) };
    }
    if (!path.startsWith("/dashboard/owner")) {
      return { executed: false, failure: failure({ failureClass: "frontend_surface", code: "route_not_allowed", surface, message: path }) };
    }
    return navigateSafely(navigate, path, state, { surface });
  }
  return {
    executed: false,
    failure: failure({ failureClass: "frontend_surface", code: "unsupported_surface", surface }),
  };
}

export function executeOwnerJarvisUi(navigate, ui) {
  return executeOwnerJarvisUiOutcome(navigate, ui).executed;
}

export function executeOwnerJarvisActionPlan(navigate, turn) {
  const actions = Array.isArray(turn?.actions) && turn.actions.length
    ? turn.actions
    : (turn?.ui ? [{ type: "render_surface", ui: turn.ui }] : []);
  const failures = [];
  let executedCount = 0;

  actions.forEach((action, index) => {
    const actionType = String(action?.type || "");
    if (actionType !== "render_surface") {
      failures.push({
        index,
        ...failure({ failureClass: "frontend_action", code: "unsupported_action_type", actionType, surface: String(action?.ui?.surface || "") }),
      });
      return;
    }
    const outcome = executeOwnerJarvisUiOutcome(navigate, action.ui);
    if (outcome.executed) executedCount += 1;
    if (outcome.failure) failures.push({ index, ...outcome.failure, actionType });
  });

  return {
    plannedCount: actions.length,
    executedCount,
    failedCount: failures.length,
    failures,
  };
}

export function executeOwnerJarvisActions(navigate, turn) {
  return executeOwnerJarvisActionPlan(navigate, turn).executedCount > 0;
}


export function ownerJarvisActionExecutionNotice(execution) {
  const failedCount = Number(execution?.failedCount || 0);
  if (!failedCount) return "";
  const executedCount = Number(execution?.executedCount || 0);
  return executedCount > 0
    ? "Una parte del plan de Jarvis no pudo abrirse en la interfaz. El fallo quedó registrado en Jarvis Lab."
    : "Jarvis entendió la solicitud, pero la interfaz no pudo ejecutar la acción. El fallo quedó registrado en Jarvis Lab.";
}

export function ownerJarvisActionExecutionFailureReport(turn, execution, { utterance = "", currentPath = "", inputSource = "unknown", conversationContext = null } = {}) {
  const raw = String(utterance || "").trim();
  const failures = Array.isArray(execution?.failures) ? execution.failures : [];
  if (!raw || !failures.length) return null;
  const primary = failures[0] || {};
  const summary = failures.slice(0, 6).map((row) => ({
    index: Number(row.index || 0),
    failureClass: String(row.failureClass || "frontend_executor"),
    code: String(row.code || "execution_failed"),
    actionType: String(row.actionType || ""),
    surface: String(row.surface || ""),
    missingFields: Array.isArray(row.missingFields) ? row.missingFields.map(String).filter(Boolean).slice(0, 12) : [],
  }));
  return {
    utterance: raw,
    currentPath: String(currentPath || ""),
    inputSource: ["voice", "text", "system"].includes(inputSource) ? inputSource : "unknown",
    source: "frontend_action_executor",
    reason: "frontend_action_execution_failed",
    sessionKind: String(turn?.session?.kind || ""),
    session: turn?.session || null,
    observedReply: [turn?.reply, turn?.question].filter(Boolean).join(" ").slice(0, 1400),
    conversationContext: conversationContext || null,
    runtimeContext: {
      authority: "backend",
      frontendInterpreter: false,
      executionFailure: {
        failureClass: String(primary.failureClass || "frontend_executor"),
        code: String(primary.code || "execution_failed"),
        actionType: String(primary.actionType || ""),
        surface: String(primary.surface || ""),
        missingFields: Array.isArray(primary.missingFields) ? primary.missingFields.map(String).filter(Boolean).slice(0, 12) : [],
        plannedCount: Number(execution?.plannedCount || 0),
        executedCount: Number(execution?.executedCount || 0),
        failedCount: Number(execution?.failedCount || failures.length),
        failures: summary,
      },
    },
  };
}
